import type {
  CommentsSortUi,
  IGetCommentResponse,
  IProjectAuthorShort,
  IProjectCommentCountersByType,
} from './projects'

export type GrowthToolStatus = 'draft' | 'published'
export type GrowthToolSourceType = 'user' | 'classic' | 'science' | 'editorial' | 'ai'
export type GrowthToolContentType = 'text' | 'audio' | 'video'

export interface IGrowthToolViewerPermissions {
  is_owner: boolean
  can_edit_content: boolean
  can_delete_growth_tool: boolean
  can_comment: boolean
  restriction_level?: 'full' | 'comments' | null
}

export type GrowthToolUserRestrictionLevel = 'full' | 'comments'

export interface IGrowthToolModerationUserCandidate extends IProjectAuthorShort {
  /** Stable id (prefer over user.id when stub has id: null). */
  user_id: number
  restrictable: boolean
  restrictable_reason?: string | null
}

export interface IGrowthToolModerationUserCandidatesResponse {
  items: IGrowthToolModerationUserCandidate[]
  total: number
}

export interface IGrowthToolUserRestrictionResource {
  user: IProjectAuthorShort
  /** Stable id for DELETE (even when user stub has id: null). */
  user_id: number
  level: GrowthToolUserRestrictionLevel
  reason: string | null
  created_at: string
  created_by: IProjectAuthorShort
}

export interface IGrowthToolViewerResource {
  is_followed: boolean
  is_liked?: boolean
  permissions?: IGrowthToolViewerPermissions
}

export interface IGrowthToolCountersResource {
  likes: number
  views: number
  subscribers: number
  comments: number | IProjectCommentCountersByType
}

export interface IGrowthToolResource {
  id: number
  author_id: number
  title: string
  slug: string
  status: GrowthToolStatus | string
  source_type: GrowthToolSourceType[] | string[]
  content_type: GrowthToolContentType[] | string[]
  description: string
  image: string | null
  /** Detail only; short list does not include gallery. */
  image_gallery?: string[] | null
  author?: IProjectAuthorShort | null
  likes_preview?: IProjectAuthorShort[] | null
  is_followed?: boolean
  viewer?: IGrowthToolViewerResource | null
  counters: IGrowthToolCountersResource
  published_at: string | null
  created_at: string
  updated_at: string
}

export interface IGrowthToolListItem extends IGrowthToolResource {
  counters: IGrowthToolCountersResource & { comments: number }
}

export type TGrowthToolsListResponse = IGrowthToolListItem[]

export type TGetGrowthToolsQueryParams = {
  query?: string
  source_type?: GrowthToolSourceType | string
  content_type?: GrowthToolContentType | string
  sort_by?: 'published_at' | 'created_at'
  sort_direction?: 'asc' | 'desc'
}

export type TGetGrowthToolsCursorQueryParams = TGetGrowthToolsQueryParams & {
  cursor?: string | null
  limit?: number
}

export type TGrowthToolsCursorPage = {
  items: TGrowthToolsListResponse
  meta: { next_cursor: string | null }
}

export type TCreateGrowthToolBody = {
  title: string
  description: string
  source_type?: GrowthToolSourceType[] | string[]
  content_type?: GrowthToolContentType[] | string[]
  status?: GrowthToolStatus
  meta?: Record<string, unknown> | null
}

export type TUpdateGrowthToolBody = Partial<TCreateGrowthToolBody>

export type GetGrowthToolCommentsQueryArg = {
  growthToolId: number
  type: string
  parentId?: number
  limit?: number
  cursor?: string | null
  sort?: CommentsSortUi
}

export type TGetGrowthToolCommentsResponse = IGetCommentResponse[]

export type TGrowthToolCommentThreadPage = {
  items: IGetCommentResponse[]
  nextCursor: string | null
}

export type GetGrowthToolCommentThreadPageArg = {
  growthToolId: number
  type: string
  parentId: number
  cursor?: string | null
  limit?: number
}

export interface IGrowthToolLikeUserResource extends IProjectAuthorShort {
  name?: string | null
}

export function resolveGrowthToolCommentsTotal(counters: IGrowthToolCountersResource['comments']): number {
  if (typeof counters === 'number') return counters
  if (counters && typeof counters === 'object') return counters.total ?? 0
  return 0
}
